// A layered layout for the influence map: the firm's people on the left, the connectors they hold a tie to in
// the middle, the people to reach on the right. Pure: nodes and edges in, positions out. Order within a layer
// is by rank first, then a barycenter sweep pulls each node toward the neighbours it is linked to so the routes
// cross as little as possible; each layer is centred on the tallest. No React, no JointJS.

export type Layer = 'owner' | 'connector' | 'target'
export const LAYERS: readonly Layer[] = ['owner', 'connector', 'target']

export interface LayoutNode {
  id: string
  layer: Layer
  /** vertical footprint in px (the disc plus its label) */
  size: number
  /** higher ranks first, before the sweep; ties keep input order */
  rank?: number
}

export interface LayoutEdge { from: string; to: string }

export interface LayoutOptions {
  /** horizontal distance between layer centrelines */
  layerGap?: number
  /** vertical gap between two footprints in a layer */
  nodeGap?: number
  /** barycenter sweeps (forward + backward pairs) */
  sweeps?: number
  /** a layer taller than this many nodes splits into side-by-side sub-columns, per layer (default: never) */
  maxPerColumn?: Partial<Record<Layer, number>>
  /** horizontal distance between two sub-columns of one layer */
  subGap?: number
}

export interface PlacedNode { id: string; layer: Layer; x: number; y: number; size: number; /** sub-column within the layer, 0-based */ column: number }
export interface LayerColumn { layer: Layer; x: number; count: number; height: number; subColumns: number }
export interface Layout { nodes: PlacedNode[]; columns: LayerColumn[]; width: number; height: number }

const DEFAULTS = { layerGap: 300, nodeGap: 10, sweeps: 2, subGap: 150 }

export function layeredLayout(nodes: LayoutNode[], edges: LayoutEdge[], options: LayoutOptions = {}): Layout {
  const o = { ...DEFAULTS, ...options }
  const known = new Set(nodes.map((n) => n.id))
  const links = edges.filter((e) => known.has(e.from) && known.has(e.to) && e.from !== e.to)

  // initial order: rank desc, then input order
  const order = new Map<Layer, LayoutNode[]>()
  for (const layer of LAYERS) {
    order.set(layer, nodes.filter((n) => n.layer === layer).map((n, i) => ({ n, i })).sort((a, b) => (b.n.rank ?? 0) - (a.n.rank ?? 0) || a.i - b.i).map((x) => x.n))
  }
  const neighbours = new Map<string, string[]>()
  for (const e of links) {
    neighbours.set(e.from, [...(neighbours.get(e.from) ?? []), e.to])
    neighbours.set(e.to, [...(neighbours.get(e.to) ?? []), e.from])
  }
  const layerOf = new Map(nodes.map((n) => [n.id, n.layer]))

  // barycenter: a node moves toward the mean index of its neighbours in the adjacent layer that was just settled
  const settle = (layer: Layer, adjacent: Layer) => {
    const list = order.get(layer)!
    const index = new Map(order.get(adjacent)!.map((n, i) => [n.id, i]))
    const bary = new Map<string, number>()
    list.forEach((n, i) => {
      const near = (neighbours.get(n.id) ?? []).filter((id) => layerOf.get(id) === adjacent).map((id) => index.get(id)!).filter((v) => v !== undefined)
      bary.set(n.id, near.length ? near.reduce((a, b) => a + b, 0) / near.length : i)   // unlinked nodes hold their place
    })
    order.set(layer, [...list].sort((a, b) => bary.get(a.id)! - bary.get(b.id)! || (b.rank ?? 0) - (a.rank ?? 0)))
  }
  for (let s = 0; s < o.sweeps; s++) {
    for (let i = 1; i < LAYERS.length; i++) settle(LAYERS[i], LAYERS[i - 1])
    for (let i = LAYERS.length - 2; i >= 0; i--) settle(LAYERS[i], LAYERS[i + 1])
  }

  // split a tall layer into sub-columns (column-major, so the order still reads top to bottom, left to right),
  // stack each sub-column, then centre everything on the tallest
  const stackHeight = (list: LayoutNode[]) => list.reduce((h, n) => h + n.size, 0) + Math.max(0, list.length - 1) * o.nodeGap
  const split = new Map<Layer, LayoutNode[][]>()
  for (const layer of LAYERS) {
    const list = order.get(layer)!
    const max = Math.max(1, o.maxPerColumn?.[layer] ?? Number.POSITIVE_INFINITY)
    const k = Math.max(1, Math.ceil(list.length / max))
    const per = Math.ceil(list.length / k)
    split.set(layer, Array.from({ length: k }, (_, ci) => list.slice(ci * per, (ci + 1) * per)).filter((c) => c.length > 0))
  }
  const heights = new Map<Layer, number>()
  for (const layer of LAYERS) heights.set(layer, Math.max(0, ...split.get(layer)!.map(stackHeight)))
  const tallest = Math.max(0, ...heights.values())
  const placed: PlacedNode[] = []
  const columns: LayerColumn[] = []
  let maxX = 0
  LAYERS.forEach((layer, li) => {
    const cols = split.get(layer)!
    const x = li * o.layerGap
    cols.forEach((list, ci) => {
      const cx = x + (ci - (cols.length - 1) / 2) * o.subGap
      maxX = Math.max(maxX, cx)
      let y = (tallest - stackHeight(list)) / 2
      for (const n of list) {
        placed.push({ id: n.id, layer, x: cx, y: y + n.size / 2, size: n.size, column: ci })
        y += n.size + o.nodeGap
      }
    })
    columns.push({ layer, x, count: order.get(layer)!.length, height: heights.get(layer)!, subColumns: Math.max(1, cols.length) })
  })
  return { nodes: placed, columns, width: nodes.length ? Math.max((LAYERS.length - 1) * o.layerGap, maxX) : 0, height: tallest }
}
